import * as React from "react";
import * as ReactDOMServer from "react-dom/server";
import * as ReactDOMClient from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ArticlePage } from "./pages/ArticlePage/ArticlePage";
import { FrontPage } from "./pages/FrontPage/FrontPage";
import { SectionPage } from "./pages/SectionPage/SectionPage";
import { BusinessStreamPage } from "./pages/BusinessStreamPage/BusinessStreamPage";
import HeaderWrapper from "./components/Header/HeaderWrapper";
import Footer from "./components/Footer/Footer";

declare global {
  interface GlobalComponents {
    FrontPage: React.FC;
    SectionPage: React.FC;
    ArticlePage: React.FC;
    BaseEditorialPage: React.FC;
    BusinessStreamPage: React.FC;
    Header: React.FC;
    Footer: React.FC;
  }

  var Components: GlobalComponents;
}

const ssrQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: Infinity,
    },
  },
});

const withQueryClient =
  (Component: React.FC<any>) =>
  (props: any) =>
    React.createElement(
      QueryClientProvider,
      { client: ssrQueryClient },
      React.createElement(Component, props)
    );

// ── SSR adapter for Header ────────────────────────────────────────────────
// Backend (SsrComponent.cs) passes: { content: { frontPage: {...}, content: {...}, headerModel: {...} } }
// HeaderWrapper expects flat props: { frontPageTitle, frontPageUrl, frontPageId, currentPageId, initialHeaderModel }
const SsrHeader: React.FC<{ content?: { frontPage?: any; content?: any; headerModel?: any } }> = ({ content }) => {
  const frontPage = content?.frontPage;
  const currentPage = content?.content;
  return React.createElement(HeaderWrapper, {
    frontPageTitle: frontPage?.name || "",
    frontPageUrl: frontPage?.url || "/",
    frontPageId: String(frontPage?.contentLink?.id || ""),
    currentPageId: String(currentPage?.contentLink?.id || ""),
    initialHeaderModel: content?.headerModel,
  });
};

// ── SSR adapter for Footer ────────────────────────────────────────────────
const SsrFooter: React.FC<{ content?: { frontPage?: any; content?: any; footerModel?: any } }> = ({ content }) => {
  const currentPageId = content?.content?.contentLink?.id ?? 0;
  return React.createElement(Footer, { currentPageId, initialFooterData: content?.footerModel });
};

export function registerSsrGlobals(): void {
  globalThis.React = React;
  globalThis.ReactDOMServer = ReactDOMServer;
  globalThis.ReactDOMClient = ReactDOMClient;
  globalThis.Components = {
    FrontPage: withQueryClient(FrontPage),
    SectionPage: withQueryClient(SectionPage),
    ArticlePage: withQueryClient(ArticlePage),
    BaseEditorialPage: withQueryClient(ArticlePage),
    BusinessStreamPage: withQueryClient(BusinessStreamPage),
    Header: withQueryClient(SsrHeader),
    Footer: withQueryClient(SsrFooter),
  };
}
